import React, { useState, useRef, useEffect, useMemo } from "react";
import { useApp, useActivities } from "../context";
import {
  Window,
  Filters,
  DemoBanner,
  Loading,
  ErrorState,
} from "../components/UI";
import ArtPoster, { PALETTES } from "../components/ArtPoster";
import { routePoints, distance, sport, sportLabel } from "../utils/activities";
export default function YourArt() {
  const { year, units } = useApp();
  const { data, loading, error, progress, retry } = useActivities();
  const [selected, setSelected] = useState([]),
    [layout, setLayout] = useState("grid"),
    [palette, setPalette] = useState("trail"),
    [search, setSearch] = useState(""),
    [type, setType] = useState("all"),
    [limit, setLimit] = useState(24),
    [saved, setSaved] = useState(false);
  const posterRef = useRef(null);
  const mapped = useMemo(() => data.filter((a) => routePoints(a)), [data]);
  const visible = mapped.filter(
    (a) =>
      (type === "all" || sport(a) === type) &&
      a.name.toLowerCase().includes(search.toLowerCase()),
  );
  const chosen = mapped.filter((a) => selected.includes(a.id));
  useEffect(() => {
    setSelected([]);
    setSearch("");
    setType("all");
    setLimit(24);
    setSaved(false);
  }, [year]);
  useEffect(() => {
    setLimit(24);
  }, [search, type]);
  const toggle = (id) => {
    setSaved(false);
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : prev.length < 100
          ? [...prev, id]
          : prev,
    );
  };
  const download = () => {
    const source = new XMLSerializer().serializeToString(posterRef.current);
    const url = URL.createObjectURL(
      new Blob([source], { type: "image/svg+xml;charset=utf-8" }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = `strava-wrapped-${year}-${layout}.svg`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setSaved(true);
  };
  return (
    <div className="page art-page">
      <DemoBanner />
      <div className="page-heading">
        <div>
          <p className="eyebrow">THE ROUTE COLLECTION</p>
          <h1>
            Made by you.
            <br />
            <em>Mile by mile.</em>
          </h1>
          <p>Your everyday routes, seen a little differently.</p>
        </div>
      </div>
      <Filters />
      {loading ? (
        <Loading progress={progress} />
      ) : error ? (
        <ErrorState error={error} retry={retry} />
      ) : (
        <div className="art-editor">
          <div className="art-controls">
            <Window title="01 / choose your routes">
              <div className="panel-content">
                <h2>Pick the good ones.</h2>
                <p>Choose up to 100 routes for your poster.</p>
                <label className="input-label" htmlFor="route-search">
                  Find an activity
                </label>
                <input
                  id="route-search"
                  type="search"
                  placeholder="Search by activity name…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <label className="input-label" htmlFor="route-sport">
                  Activity type
                </label>
                <select
                  id="route-sport"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                >
                  <option value="all">All activity types</option>
                  {[...new Set(mapped.map(sport))].sort().map((t) => (
                    <option key={t} value={t}>
                      {sportLabel(t)}
                    </option>
                  ))}
                </select>
                <div className="selection-actions">
                  <span>{chosen.length} selected</span>
                  <button
                    className="text-button"
                    disabled={!visible.length}
                    onClick={() => {
                      setSelected(visible.slice(0, 100).map((a) => a.id));
                      setSaved(false);
                    }}
                  >
                    Select {visible.length > 100 ? "first 100" : "shown"}
                  </button>
                  <button
                    className="text-button"
                    onClick={() => {
                      setSelected([]);
                      setSaved(false);
                    }}
                    disabled={!chosen.length}
                  >
                    Clear
                  </button>
                </div>
                {selected.length === 100 && (
                  <p className="fine-print" role="status">
                    100-route limit reached. Deselect a route to add another.
                  </p>
                )}
                <div className="activity-picker">
                  {visible.slice(0, limit).map((a) => (
                    <button
                      className="route-choice"
                      key={a.id}
                      aria-pressed={selected.includes(a.id)}
                      onClick={() => toggle(a.id)}
                    >
                      <svg viewBox="0 0 200 200" aria-hidden="true">
                        <polyline
                          points={routePoints(a)}
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                      </svg>
                      <span>
                        <b>{a.name}</b>
                        <small>
                          {a.start_date_local.slice(0, 10)} ·{" "}
                          {distance(a.distance, units)} {units}
                        </small>
                      </span>
                      <span className="choice-check" aria-hidden="true">
                        {selected.includes(a.id) ? "✓" : "+"}
                      </span>
                    </button>
                  ))}
                </div>
                {visible.length > limit && (
                  <button
                    className="button"
                    onClick={() => setLimit((v) => v + 24)}
                  >
                    Show more routes ({visible.length - limit} left)
                  </button>
                )}
                {!visible.length && (
                  <div className="picker-empty">
                    <h3>
                      {!mapped.length
                        ? "No GPS routes this year."
                        : "No matching routes."}
                    </h3>
                    <p>
                      {!mapped.length
                        ? "Indoor and manual activities still count in your recap. Pick another year to find outdoor routes."
                        : "Try another search or activity type."}
                    </p>
                  </div>
                )}
                {mapped.length < data.length && (
                  <p className="fine-print">
                    {data.length - mapped.length} activities have no usable GPS
                    route and aren’t shown here.
                  </p>
                )}
              </div>
            </Window>
          </div>
          <div className="art-preview">
            <Window title="02 / make it yours">
              <div className="preview-toolbar">
                <div>
                  <span className="input-label">Layout</span>
                  <div className="segmented">
                    {["grid", "overlap"].map((s) => (
                      <button
                        key={s}
                        aria-pressed={layout === s}
                        onClick={() => {
                          setLayout(s);
                          setSaved(false);
                        }}
                      >
                        {s === "grid" ? "Grid" : "Overlap"}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="input-label" htmlFor="palette">
                    Palette
                  </label>
                  <select
                    id="palette"
                    value={palette}
                    onChange={(e) => {
                      setPalette(e.target.value);
                      setSaved(false);
                    }}
                  >
                    {Object.entries(PALETTES).map(([key, p]) => (
                      <option key={key} value={key}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="poster-stage">
                <ArtPoster
                  activities={chosen}
                  year={year}
                  layout={layout}
                  palette={palette}
                  posterRef={posterRef}
                />
              </div>
              <div className="download-bar">
                <span>{chosen.length} routes · ready for your wall</span>
                <button
                  className="button primary"
                  disabled={!chosen.length}
                  onClick={download}
                >
                  Download SVG ↓
                </button>
              </div>
            </Window>
            <p className="fine-print">
              SVG stays sharp at any size. Overlap centers each route
              individually. Your artwork contains route shapes; review it before
              sharing.
            </p>
            {saved && (
              <p role="status" className="saved-message">
                ✓ Your poster download is ready.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
